import React from 'react';
import { Link } from 'react-router-dom';
import { SiteLayout } from '../components/site/SiteLayout';
import { ArrowLeft, Shield } from 'lucide-react';

const TermsOfService = () => {
  return (
    <SiteLayout testid="terms-page">

      {/* Content */}
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 md:py-20">
        <Link to="/" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="glass-panel rounded-3xl p-8 md:p-12 mb-10">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-display">Terms of Service</h1>
              <p className="text-slate-400 mt-2">Last Updated: January 2026</p>
            </div>
          </div>
        </div>

        <div className="max-w-none">
          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">SMS Messaging Terms</h2>
            
            <div className="bg-orange-500/10 rounded-2xl p-6 border-l-4 border-l-orange-500 mb-6">
              <p className="text-white font-display font-semibold text-lg">
                ⚠️ Age Requirement: You must be 18+ to use this SMS service.
              </p>
            </div>
            
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Zivae Financial SMS Program</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              By opting into our SMS program, you will receive text messages from Zivae Financial containing:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li>Appointment reminders and confirmations</li>
              <li>Order and delivery updates</li>
              <li>Service updates and important notifications</li>
              <li>Special offers, discounts, and new product updates (if you opt-in to marketing messages)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">How to Opt-Out</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              You can cancel the SMS service at any time. Just text <strong>"STOP"</strong> to <strong>(227) 238-1080</strong>. After you send the SMS message "STOP" to us, we will send you an SMS message to confirm that you have been unsubscribed. After this, you will no longer receive SMS messages from us.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you want to join again, just sign up as you did the first time and we will start sending SMS messages to you again.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Getting Help</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you are experiencing issues with the messaging program, you can reply with the keyword <strong>"HELP"</strong> for more assistance, or you can get help directly at:
            </p>
            <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-orange-500 mb-4">
              <p className="text-white font-display font-semibold mb-2">Contact Support</p>
              <p className="text-slate-300">Phone: <a href="tel:2272381080" className="text-orange-400 hover:text-orange-300">(227) 238-1080</a></p>
              <p className="text-slate-300">Virtual Nationwide Service</p>
            </div>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Carrier Liability</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              Carriers are not liable for delayed or undelivered messages.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Message and Data Rates</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              As always, message and data rates may apply for any messages sent to you from us and to us from you. Message frequency varies based on your activity and the services you use.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have any questions about your text plan or data plan, it is best to contact your wireless provider.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Privacy</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have any questions regarding privacy, please read our privacy policy:
            </p>
            <div className="mb-4">
              <Link to="/privacy-policy" className="text-orange-400 hover:text-orange-300 underline font-semibold">
                View Our Privacy Policy
              </Link>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">General Terms of Service</h2>
            
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Acceptance of Terms</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              By accessing and using the Zivae Financial website and services, you accept and agree to be bound by the terms and provision of this agreement.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Use of Services</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              Our services are intended for individuals seeking retirement planning and financial consulting. You agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li>Provide accurate and complete information when using our services</li>
              <li>Keep your account information secure and confidential</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Use our services only for lawful purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Professional Advice Disclaimer</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              The information provided through our website and services is for general informational purposes only. While we strive to provide accurate and up-to-date information, we make no representations or warranties of any kind about the completeness, accuracy, reliability, or suitability of the information.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              Consultation with Zivae Financial's certified retirement consultants is recommended before making any financial decisions.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Intellectual Property</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              All content, trademarks, logos, and intellectual property on this website are owned by Zivae Financial or its licensors. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Limitation of Liability</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              To the fullest extent permitted by law, Zivae Financial shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Changes to Terms</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              We reserve the right to modify these Terms of Service at any time. We will notify you of any material changes by posting the updated terms on our website with a new "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-white font-display mb-3">Contact Information</h3>
            <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-orange-500">
              <p className="text-white font-display font-semibold mb-2">Zivae Financial</p>
              <p className="text-slate-300">Virtual Nationwide</p>
              <p className="text-slate-300">Phone: <a href="tel:2272381080" className="text-orange-400 hover:text-orange-300">(227) 238-1080</a></p>
              <p className="text-slate-300 mt-2">Licensed in 19 states: CA, FL, GA, IL, IN, KY, MD, ME, MI, NC, NM, NY, OH, OR, PA, SC, TX, VA, WI</p>
            </div>
          </section>

          <section className="bg-orange-500/10 rounded-2xl p-6 border border-orange-400/30">
            <h3 className="text-xl font-semibold text-white font-display mb-3">Agreement</h3>
            <p className="text-slate-300 leading-relaxed">
              By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </div>
    </SiteLayout>
  );
};

export default TermsOfService;
